import type { ISchedulerTask, ITaskLocation, LocationStatus, WritableLocationStatus } from '../../interfaces/IScheduler.js';
import { countDone, progressAfterReport, areaProgress } from '../../../area-status/progress.js';

// The derivation itself lives beside the area vocabulary, where the server
// reads it too (it derives the % an area write moves — QA R-04); the session
// below is the clients' own.
export { areaProgress, blendProgress, countDone, progressAfterReport } from '../../../area-status/progress.js';

// ---------------------------------------------------------------------------
// Task progress from area completion.
//
// A leaf task's progress (`progressPercent`) is area-driven. Within one drawer
// session the Overview slider can OVERRIDE it — dragging freezes the % against
// further area changes for as long as the drawer stays open. The override is
// session-only: re-opening the drawer starts fresh with the STORED % as the
// new base, and the not-yet-completed areas split the REMAINING percentage
// equally. E.g. 2 of 10 areas done reads 20 %, slide to 30 % and it stays
// 30 % until the drawer closes; on re-open the 8 remaining areas are worth
// (100 − 30) / 8 = 8.75 % each, so completing one moves it to 39 %.
//
// Pure functions — no React, no store; unit-tested in isolation.
// ---------------------------------------------------------------------------


/**
 * A progress session — the snapshot an area report blends from. The web
 * drawer and the phone sheet open one per visit (the slider can take MANUAL
 * control for the rest of it); the Progress Tracker opens one per tap.
 */
export interface IProgressSession {
  /** The stored % when the session opened. */
  base: number;
  /** The areas already done then. */
  baseCompleted: number;
  /** A value was set by hand: area reports no longer move the %. */
  manual: boolean;
}

/** The session a visit to `task` starts from: its stored %, area-driven. */
export function openProgressSession(task: Pick<ISchedulerTask, 'progressPercent' | 'locations'>): IProgressSession {
  return { base: task.progressPercent, baseCompleted: countDone(task.locations ?? []), manual: false };
}

/** What reporting some areas as one status comes to — {@link applyAreaReport}. */
export interface IAreaReport {
  /** The reported areas that are on the task and read something else now —
   *  each one write. Empty: the report is a no-op. */
  targets: string[];
  /** The task's areas after the report. A changed area drops its stored
   *  per-area `progressPercent`: the new status is the truth there, and a stale
   *  % would override it (the tracker's cell % and floor rollup). */
  nextLocations: ITaskLocation[];
  /** The task % to write — `null` while the session is manual or when the
   *  done count didn't move ({@link progressAfterReport}). */
  pct: number | null;
}

/**
 * Report `zoneIds` as `status` against `session`. An id that isn't one of the
 * task's areas, or an area that already reads `status`, is no report.
 */
export function applyAreaReport(
  session: IProgressSession,
  locations: readonly ITaskLocation[],
  zoneIds: readonly string[],
  status: WritableLocationStatus,
): IAreaReport {
  const current = new Map(locations.map((location) => [location.locationId, location.status]));
  const targets = [...new Set(zoneIds)].filter((id) => current.has(id) && current.get(id) !== status);
  const changed = new Set(targets);
  const nextLocations = locations.map((location) => {
    if (!changed.has(location.locationId)) return location;
    const { progressPercent: _stale, ...rest } = location;
    return { ...rest, status };
  });
  const pct = session.manual
    ? null
    : progressAfterReport(session.base, session.baseCompleted, countDone(locations), countDone(nextLocations), nextLocations.length);
  return { targets, nextLocations, pct };
}

/**
 * "Use area progress": the session re-anchored on the plain area % — its
 * `base` is the % to write, and from there each area is worth `100 / total`
 * again. `null` when the task has no areas to take it from.
 */
export function resetToAreaProgress(locations: readonly { status: LocationStatus }[]): IProgressSession | null {
  const baseCompleted = countDone(locations);
  const base = areaProgress(baseCompleted, locations.length);
  return base === null ? null : { base, baseCompleted, manual: false };
}

/** What drives the task % — the line under the slider. */
export type ProgressState = 'noAreas' | 'manual' | 'auto';

/** i18n key for each {@link ProgressState} line. */
export const PROGRESS_STATE_LABEL_KEY: Record<ProgressState, string> = {
  noAreas: 'scheduler.panel.progress.noAreas',
  manual: 'scheduler.panel.progress.manualOverride',
  auto: 'scheduler.panel.progress.autoFromAreas',
};

/** No areas → the slider is the whole report; else the session says whether a
 *  value set by hand holds the %, or the areas drive it. */
export function progressState(total: number, manual: boolean): ProgressState {
  if (total === 0) return 'noAreas';
  return manual ? 'manual' : 'auto';
}
